// src/components/NotificationBell.jsx
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  async function load() {
    try {
      const body = await api('/admin/notifications');
      setItems(body.notifications);
    } catch { /* non-critical — silent */ }
  }

  useEffect(() => {
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    function onClickOutside(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const unread = items.filter((n) => !n.read_at).length;

  async function openItem(n) {
    if (!n.read_at) await api(`/admin/notifications/${n.id}/read`, { method: 'POST' }).catch(() => {});
    setOpen(false);
    load();
    if (n.case_ref) navigate('/'); // queue view — case detail needs the case id, not just ref; ref is shown for now
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Notifications"
        className="relative h-9 w-9 rounded-full hover:bg-ink/5 flex items-center justify-center text-ink/60"
      >
        🔔
        {unread > 0 && (
          <span className="absolute top-0.5 right-0.5 h-4 min-w-4 px-1 rounded-full bg-p1 text-white text-[10px] flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-lg border border-ink/10 bg-white shadow-lg z-30 max-h-96 overflow-y-auto">
          <div className="px-3 py-2 border-b border-ink/10 text-xs font-semibold text-ink/50 uppercase tracking-wide">Notifications</div>
          {items.length === 0 && <div className="px-3 py-4 text-xs text-ink/40 text-center">Nothing yet.</div>}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => openItem(n)}
              className={`w-full text-left px-3 py-2.5 text-xs border-b border-ink/5 hover:bg-paper transition-colors ${!n.read_at ? 'bg-accent-100/40' : ''}`}
            >
              <div className="font-medium text-charcoal capitalize">{n.type.replace('_', ' ')}</div>
              {n.case_ref && <div className="font-mono text-ink/50 mt-0.5">{n.case_ref}</div>}
              <div className="text-ink/40 mt-0.5">{new Date(n.created_at).toLocaleString()}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
